import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  author?: string;
  ogImage?: string;
  canonicalPath?: string;
  ogType?: string;
}

const BASE_URL = 'https://greentick.org.in';

export function useSEO({ 
  title, 
  description, 
  keywords,
  author = 'GreenTick AI',
  ogImage = '/og-image.jpg',
  canonicalPath, 
  ogType = 'website' 
}: SEOProps) {
  useEffect(() => {
    // Determine the current path if not provided
    const currentPath = canonicalPath || window.location.pathname;
    const fullCanonicalUrl = `${BASE_URL}${currentPath.startsWith('/') ? '' : '/'}${currentPath}`;

    // Set document title
    document.title = title;

    // Update or create meta description
    let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = description;

    // Update keywords
    let metaKeywords = document.querySelector<HTMLMetaElement>('meta[name="keywords"]');
    if (!metaKeywords && keywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    if (metaKeywords && keywords) metaKeywords.content = keywords;

    // Update author
    let metaAuthor = document.querySelector<HTMLMetaElement>('meta[name="author"]');
    if (!metaAuthor) {
      metaAuthor = document.createElement('meta');
      metaAuthor.name = 'author';
      document.head.appendChild(metaAuthor);
    }
    metaAuthor.content = author;

    // Update OG title
    let ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
    if (ogTitle) ogTitle.content = title;

    // Update OG description
    let ogDesc = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
    if (ogDesc) ogDesc.content = description;

    // Update OG image
    let ogImg = document.querySelector<HTMLMetaElement>('meta[property="og:image"]');
    if (ogImg) {
      ogImg.content = ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;
    }

    // Update OG type
    let ogTypeTag = document.querySelector<HTMLMetaElement>('meta[property="og:type"]');
    if (ogTypeTag) ogTypeTag.content = ogType;

    // Update OG url
    let ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    if (ogUrl) ogUrl.content = fullCanonicalUrl;

    // Update Twitter tags
    let twitterTitle = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.content = title;

    let twitterDesc = document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]');
    if (twitterDesc) twitterDesc.content = description;

    let twitterImg = document.querySelector<HTMLMetaElement>('meta[name="twitter:image"]');
    if (twitterImg) {
      twitterImg.content = ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;
    }

    // Update canonical link
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = fullCanonicalUrl;

    // Reset on unmount
    return () => {
      // We don't necessarily want to remove the canonical tag, just reset the title
      document.title = 'GreenTick AI - Hire the Right Person. In 48 Hours.';
      const defaultDesc = 'Hire the right person in 48 hours. GreenTick AI conducts deep behavioral assessments, verifies skills, and delivers comprehensive reports with zero bias.';
      if (metaDesc) metaDesc.content = defaultDesc;
    };
  }, [title, description, keywords, author, ogImage, canonicalPath, ogType]);
}
